import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Checkbox,
  FormControl,
  Slide,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import {
  TransactionsCategory,
  TransactionsType
} from 'src/utils/constants/transactions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { CREATE_MANUAL_TRANSACTION } from 'src/store/actions/transactionActions';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import { loadPendingPaymentIncomeCFDIsForClient } from 'src/store/actions/cfdiActions';
import TransactionDialogHeader from '../TransactionDialogHeader';
import CfdiAmountRowList from '../CfdiAmountRowList';
import FieldLabel from '../FieldLabel';
import makeInitialState from './initialState';
import validationSchema from './schema';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const invalidFieldSx = {
  '& .MuiFormLabel-root.Mui-error': { color: 'secondary.main' },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: 'secondary.main'
  },
  '& .MuiFormHelperText-root.Mui-error': { color: 'secondary.main' }
};

const getInvoiceOptionLabel = (t, option) =>
  option
    ? `${t('COMMON.CONTRACT')} ${option.contract.folio} - ${t(
        'COMMON.SERIE'
      )} ${option.series} - ${option.folio} - $${formatAmountWithDecimals(
        option?.amountIvaIncluded,
        2
      )}`
    : '';

const GeneratDiscountDialog = ({ open, handleClose, submit, clientId }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [cfdiList, setCfdiList] = useState([]);
  const loading = useSelector(
    isLoadingSelector([CREATE_MANUAL_TRANSACTION.action])
  );
  const isLoadingCfdis = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );
  const initialState = makeInitialState();
  const resetRef = useRef();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(loadPendingPaymentIncomeCFDIsForClient(clientId, setCfdiList));
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
      setCfdiList([]);
    };
  }, [resetRef, dispatch, id, open]);

  const handleSubmitTransaction = useCallback(
    values => {
      const relatedInvoices = values.cfdis
        .filter(row => row.cfdi)
        .map(row => ({ invoiceId: row.cfdi.id, amount: row.amount }));

      // Don't reset here — the form must stay visible with its values while
      // the request is in flight. The dialog's own close effect resets the
      // form once it actually closes.
      submit(
        {
          relatedInvoices,
          comment: values.comment,
          doNotBillTransaction: values.doNotBillTransaction,
          type: TransactionsType.PAYMENT,
          category: TransactionsCategory.DISCOUNT
        },
        handleClose
      );
    },
    [submit, handleClose]
  );

  const handleCloseDialog = () => {
    handleClose();
    setCfdiList([]);
  };

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: 680 } }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <TransactionDialogHeader
        title={t('COMMON.REGISTER_DISCOUNT')}
        subtitle={t('TRANSACTION.CREATE_DISCOUNT_SUBTITLE')}
        onClose={handleCloseDialog}
      />
      <DialogContent style={{ marginBottom: '16px' }}>
        <Formik
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={handleSubmitTransaction}
          enableReinitialize
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue
          }) => {
            resetRef.current = () => resetForm();
            return (
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <CfdiAmountRowList
                    rows={values.cfdis}
                    availableCfdis={cfdiList}
                    isLoadingCfdis={isLoadingCfdis}
                    errors={errors.cfdis}
                    touched={Boolean(touched.cfdis)}
                    getOptionLabel={option => getInvoiceOptionLabel(t, option)}
                    onChange={rows => setFieldValue('cfdis', rows)}
                    cfdiLabel={t('COMMON.CFDI')}
                    totalLabel={t('TRANSACTION.TOTAL_AMOUNT')}
                  />
                </FormControl>

                <FieldLabel label={t('COMMON.INVOICE_CONCEPT')}>
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    sx={invalidFieldSx}
                    error={Boolean(touched.comment && errors.comment)}
                    fullWidth
                    helperText={touched.comment && t(errors.comment)}
                    margin="none"
                    name="comment"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.comment}
                    variant="outlined"
                  />
                </FieldLabel>

                <Box sx={{ mt: 2.5 }}>
                  <FormControlLabel
                    key="do-not-bill-transaction"
                    control={
                      <Checkbox
                        name="doNotBillTransaction"
                        color="primary"
                        checked={values.doNotBillTransaction}
                        onChange={() => {
                          setFieldValue(
                            'doNotBillTransaction',
                            !values.doNotBillTransaction
                          );
                        }}
                      />
                    }
                    label={t('TRANSACTION.DO_NOT_BILL_TRANSACTION')}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'text.secondary',
                      ml: 4,
                      mt: -0.5
                    }}
                  >
                    {t('TRANSACTION.DO_NOT_BILL_DISCOUNT_HELP')}
                  </Typography>
                </Box>

                <Divider sx={{ mt: 3, mb: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      resetForm(initialState);
                      handleCloseDialog();
                    }}
                    disabled={loading}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={
                      loading ||
                      isLoadingCfdis ||
                      !values.cfdis.some(row => row.cfdi)
                    }
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ? t('COMMON.REGISTERING') : t('COMMON.REGISTER')}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

GeneratDiscountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  clientId: PropTypes.number
};

export default GeneratDiscountDialog;
